import {
  ApolloError,
  gql,
  useMutation,
} from "@apollo/client";
import { 
    DeleteCampaignByIdMutationMutation,
    DeleteCampaignByIdMutationMutationVariables 
} from "../../../../../schemaTypes";
import { NormalizedErrorMap } from "../../../../../types/NormalizedErrorMap";
import { normalizeErrors } from "../../../../../utils/normalizeErrors";

export const deleteCampaignByIdMutation = gql`
  mutation DeleteCampaignByIdMutation(
    $id: String!
  ) {
    deleteCampaignById(
      id: $id
    ) {
      errors {
        path
        message
      }
      message
    }
  }
`;

interface Props {
  onMessage?: (message: string) => void;
  children: (
    data: {
      submit: (
        values: DeleteCampaignByIdMutationMutationVariables
      ) => Promise<{
        errors: NormalizedErrorMap | null;
        message?: string;
      }>;
      loading: boolean;
      error?: ApolloError;
    }
  ) => React.ReactElement | null;
}

export const DeleteCampaignByIController: React.FC<Props> = ({
  children,
  onMessage,
}) => {
  const [deleteCampaignByIdMutationFn, { loading, error }] =
    useMutation<
      DeleteCampaignByIdMutationMutation,
      DeleteCampaignByIdMutationMutationVariables
    >(deleteCampaignByIdMutation);

  const submit = async (
    values: DeleteCampaignByIdMutationMutationVariables
  ) => {
    const id = typeof values.id === "string" ? values.id.trim() : "";
    if (!id) {
      return {
        errors: normalizeErrors([
          { path: "id", message: "Campaign id is required" },
        ]),
        message: undefined,
      };
    }

    const response = await deleteCampaignByIdMutationFn({
      variables: { ...values, id },
    });

    const data = response.data?.deleteCampaignById;

    const errors = data?.errors
      ? normalizeErrors((data.errors || []).filter((e): e is Exclude<typeof e, null> => e !== null))
      : null;

    if (data?.message && onMessage) {
      onMessage(data.message);
    }

    return {
      errors,
      message: data?.message ?? undefined,
    };
  };

  return children({ submit, loading, error });
};